import _ttmlFunc from "./ttmlGeneral.js";
import _rubyMarkup from "../quill/rubyMarkup.js";
import _quillClasses from "../../dict/quillClasses.js";
import _convertToHtml from "../quill/convertToHtml.js";
/**
 * IMSC Rosetta helpers: style catalog, region quantisation, span ↔ Event.text conversion.
 * Spec: https://github.com/imsc-rosetta/imsc-rosetta-specification
 */
const convertToHtml = _convertToHtml;
const quillClasses = _quillClasses;
const rubyMarkup = _rubyMarkup;
const ttmlFunc = _ttmlFunc;
const FG_COLORS = {
  s_fg_black: "black",
  s_fg_red: "red",
  s_fg_yellow: "yellow",
  s_fg_green: "lime",
  s_fg_cyan: "cyan",
  s_fg_blue: "blue",
  s_fg_magenta: "magenta",
  s_fg_white: "white"
};
const FG_COLOR_TO_STYLE = {
  black: "s_fg_black",
  red: "s_fg_red",
  yellow: "s_fg_yellow",
  green: "s_fg_green",
  lime: "s_fg_green",
  cyan: "s_fg_cyan",
  blue: "s_fg_blue",
  magenta: "s_fg_magenta",
  white: "s_fg_white"
};
const BOUTEN_MAP = {
  s_emf_fco: "bouten-fco",
  s_emf_fdo: "bouten-fdo",
  s_emf_fso: "bouten-fso",
  s_emf_oco: "bouten-oco",
  s_emf_odo: "bouten-odo",
  s_emf_oso: "bouten-oso"
};
const BOUTEN_TO_STYLE = {
  "bouten-fco": "s_emf_fco",
  "bouten-fdo": "s_emf_fdo",
  "bouten-fso": "s_emf_fso",
  "bouten-oco": "s_emf_oco",
  "bouten-odo": "s_emf_odo",
  "bouten-oso": "s_emf_oso"
};
const ALIGN_STYLES = {
  p_al_start: "left",
  p_al_end: "right",
  p_al_center: "center",
  p_al_start_center: "left",
  p_al_start_end: "left",
  p_al_end_start: "right",
  p_al_end_center: "right",
  p_al_center_start: "center",
  p_al_center_end: "center"
};
const ALIGN_TO_STYLE = {
  left: "p_al_start",
  start: "p_al_start",
  right: "p_al_end",
  end: "p_al_end",
  center: "p_al_center"
};
const BOXED_RE = /^ps_bg_boxed(\w+)$/;
const GHOST_BOXED_RE = /^ps_bg_ghostboxed(\w+)$/;
const DEFAULT_QUANT = {
  originX: 10,
  originY: 10,
  extentW: 80,
  extentH: 80,
  fontSizeRh: 5.333,
  lineHeightPct: 125
};
function parseStyleList(styleAttr) {
  if (!styleAttr) {
    return [];
  }
  return String(styleAttr).trim().split(/\s+/).filter(Boolean);
}
function ensureArray(value) {
  if (value === undefined || value === null) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
}
function parsePercentPair(value) {
  if (!value) {
    return null;
  }
  const parts = String(value).trim().split(/\s+/);
  if (parts.length < 2) {
    return null;
  }
  return {
    x: parseFloat(parts[0]),
    y: parseFloat(parts[1])
  };
}
function parseQuantisationRegion(styles) {
  const style = styles.find(s => s["@_xml:id"] === "_r_quantisationregion");
  if (!style) {
    return {
      ...DEFAULT_QUANT
    };
  }
  const origin = parsePercentPair(style["@_tts:origin"]) || {
    x: DEFAULT_QUANT.originX,
    y: DEFAULT_QUANT.originY
  };
  const extent = parsePercentPair(style["@_tts:extent"]) || {
    x: DEFAULT_QUANT.extentW,
    y: DEFAULT_QUANT.extentH
  };
  const fontSize = parseFloat(String(style["@_tts:fontSize"] || "").replace("rh", "")) || DEFAULT_QUANT.fontSizeRh;
  const lineHeight = parseFloat(String(style["@_tts:lineHeight"] || "").replace("%", "")) || DEFAULT_QUANT.lineHeightPct;
  return {
    originX: origin.x,
    originY: origin.y,
    extentW: extent.x,
    extentH: extent.y,
    fontSizeRh: fontSize,
    lineHeightPct: lineHeight
  };
}
function etherealLineCount(quant) {
  const lineHeightFraction = quant.fontSizeRh / 100 * (quant.lineHeightPct / 100);
  return Math.max(1, Math.round(quant.extentH / 100 / lineHeightFraction));
}
function lineHeightPercent(quant) {
  const lines = etherealLineCount(quant);
  return quant.extentH / lines;
}

/**
 * Map region origin/extent/displayAlign to Event yPos (line number or start/end/center).
 */
function regionToPosition(region, quant, winHeight = 15) {
  if (!region) {
    return {
      xPos: "center",
      yPos: "end",
      writingMode: "horizontal"
    };
  }
  const styleIds = parseStyleList(region["@_style"]);
  const writingMode = styleIds.includes("r_vertical") ? "tbrl" : "horizontal";
  const origin = parsePercentPair(region["@_tts:origin"]);
  const extent = parsePercentPair(region["@_tts:extent"]);
  const displayAlign = region["@_tts:displayAlign"] || "after";
  const rowH = lineHeightPercent(quant);
  let yPos = "end";
  if (displayAlign === "before" && origin) {
    // Top-anchored: row from top of safe area
    const rowsFromTop = Math.round((origin.y - quant.originY) / rowH);
    if (rowsFromTop <= 0) {
      yPos = "start";
    } else {
      yPos = Math.min(winHeight, Math.max(1, rowsFromTop + 1));
    }
  } else if (displayAlign === "center") {
    yPos = "center";
  } else if (origin && extent) {
    // Bottom-anchored: compute last line from bottom of region
    const bottom = origin.y + extent.y;
    const safeBottom = quant.originY + quant.extentH;
    const rowsFromBottom = Math.round((safeBottom - bottom) / rowH);
    if (rowsFromBottom <= 0) {
      yPos = "end";
    } else {
      const line = etherealLineCount(quant) - rowsFromBottom;
      yPos = Math.min(winHeight, Math.max(1, line));
    }
  }
  let xPos = "center";
  if (writingMode === "tbrl" && origin && extent) {
    // Vertical: left vs right of safe area
    const mid = origin.x + extent.x / 2;
    const safeMid = quant.originX + quant.extentW / 2;
    if (mid < safeMid - 5) {
      xPos = "start";
    } else if (mid > safeMid + 5) {
      xPos = "end";
    } else {
      xPos = "center";
    }
  }
  return {
    xPos,
    yPos,
    writingMode
  };
}

/**
 * Build region origin/extent for an event position.
 */
function positionToRegion(event, quant, lineCount = 1) {
  const rowH = lineHeightPercent(quant);
  const lines = etherealLineCount(quant);
  const writingMode = event.writingMode === "tbrl" ? "tbrl" : "horizontal";
  const isVertical = writingMode === "tbrl";
  let originX = quant.originX;
  let originY = quant.originY;
  let extentW = quant.extentW;
  let extentH = quant.extentH;
  let displayAlign = "after";
  let regionKey;
  if (isVertical) {
    displayAlign = "before";
    extentH = quant.extentH;
    originY = quant.originY;
    // Column width ≈ rowH adjusted for 16:9 (per spec)
    const colW = parseFloat((quant.extentW / Math.round(lines * (16 / 9))).toFixed(1)) || rowH;
    if (event.xPos === "start") {
      originX = quant.originX;
      extentW = parseFloat((quant.extentW - colW).toFixed(1));
      regionKey = `V_L`;
    } else if (event.xPos === "end") {
      originX = parseFloat((quant.originX + colW).toFixed(1));
      extentW = parseFloat((quant.extentW - colW).toFixed(1));
      regionKey = `V_R`;
    } else {
      regionKey = `V_C`;
    }
    // Full-height vertical column against an edge is more common
    if (event.xPos === "start") {
      originX = quant.originX;
      extentW = parseFloat(colW.toFixed(1));
      regionKey = "VL";
    } else {
      extentW = parseFloat(colW.toFixed(1));
      originX = parseFloat((quant.originX + quant.extentW - extentW).toFixed(1));
      regionKey = "VR";
    }
  } else if (event.yPos === "start") {
    displayAlign = "before";
    originY = quant.originY;
    extentH = quant.extentH;
    regionKey = "top";
  } else if (event.yPos === "center") {
    displayAlign = "center";
    regionKey = "center";
  } else if (!isNaN(parseInt(event.yPos, 10))) {
    const startLine = parseInt(event.yPos, 10);
    const rowsFromTop = Math.max(0, startLine - 1);
    if (rowsFromTop <= Math.floor(lines / 2)) {
      displayAlign = "before";
      originY = parseFloat((quant.originY + rowsFromTop * rowH).toFixed(1));
      extentH = parseFloat((quant.extentH - rowsFromTop * rowH).toFixed(1));
      regionKey = `T${startLine}`;
    } else {
      displayAlign = "after";
      const rowsFromBottom = Math.max(0, lines - (startLine + lineCount - 1));
      extentH = parseFloat((quant.extentH - rowsFromBottom * rowH).toFixed(1));
      originY = quant.originY;
      regionKey = `B${startLine}`;
    }
  } else {
    displayAlign = "after";
    regionKey = "bottom";
  }
  return {
    regionKey,
    writingMode,
    markup: {
      origin: `${originX}% ${originY}%`,
      extent: `${extentW}% ${extentH}%`,
      displayAlign,
      style: isVertical ? "r_default r_vertical" : "r_default"
    }
  };
}
function getNodeText(node) {
  if (node === undefined || node === null) {
    return "";
  }
  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }
  if (node["#text"] !== undefined) {
    return String(node["#text"]);
  }
  return "";
}
function isBrSpan(span) {
  if (!span || typeof span !== "object") {
    return false;
  }
  if (span.br !== undefined && !getNodeText(span) && !span.span) {
    return true;
  }
  return false;
}
function isRubyContainer(styleIds) {
  return styleIds.some(id => id === "s_rb_algn_center" || id === "s_rb_algn_around" || id === "s_rb_posn_outside");
}

/**
 * Convert a Rosetta <p> span tree into internal HTML fragment (no outer <p>).
 */
function spansToHtml(pNode) {
  const spans = ensureArray(pNode.span);
  let html = "";
  spans.forEach(span => {
    html += spanToHtml(span);
  });
  // Also handle direct #text (should not happen in valid Rosetta)
  if (!spans.length && pNode["#text"]) {
    html += String(pNode["#text"]);
  }
  return html;
}
function spanToHtml(span) {
  if (isBrSpan(span)) {
    return "\n";
  }
  const styleIds = parseStyleList(span["@_style"]);
  const nested = ensureArray(span.span);

  // Ruby container
  if (isRubyContainer(styleIds) && nested.length) {
    let base = "";
    let rubyText = "";
    let align = styleIds.includes("s_rb_algn_around") ? "around" : "center";
    nested.forEach(child => {
      const childStyles = parseStyleList(child["@_style"]);
      if (childStyles.includes("s_rb_b") || childStyles.includes("s_rb_t") === false && !childStyles.includes("s_rb_t")) {
        if (childStyles.includes("s_rb_t")) {
          rubyText = getNodeText(child);
        } else if (childStyles.includes("s_rb_b")) {
          base = getNodeText(child);
        } else {
          base += getNodeText(child);
        }
      } else if (childStyles.includes("s_rb_t")) {
        rubyText = getNodeText(child);
      } else if (childStyles.includes("s_rb_b")) {
        base = getNodeText(child);
      }
    });
    // Re-scan more carefully
    base = "";
    rubyText = "";
    nested.forEach(child => {
      const childStyles = parseStyleList(child["@_style"]);
      if (childStyles.includes("s_rb_t")) {
        rubyText = getNodeText(child);
      } else if (childStyles.includes("s_rb_b")) {
        base = getNodeText(child);
      }
    });
    return `<ruby data-align="${align}">${base}<rt>${rubyText}</rt></ruby>`;
  }
  let text = getNodeText(span);
  if (nested.length) {
    text = nested.map(n => spanToHtml(n)).join("");
  }
  if (!text && !nested.length) {
    return "";
  }
  let open = "";
  let close = "";
  if (styleIds.includes("s_bold")) {
    open += "<strong>";
    close = "</strong>" + close;
  }
  if (styleIds.includes("s_italic")) {
    open += "<em>";
    close = "</em>" + close;
  }
  if (styleIds.includes("s_underline")) {
    open += "<u>";
    close = "</u>" + close;
  }
  styleIds.forEach(id => {
    if (FG_COLORS[id]) {
      open += `<${FG_COLORS[id]}>`;
      close = `</${FG_COLORS[id]}>` + close;
    }
    const boxed = id.match(BOXED_RE);
    const ghost = id.match(GHOST_BOXED_RE);
    if (boxed) {
      const color = boxed[1];
      const tag = `bg${color.charAt(0).toUpperCase()}${color.slice(1)}`;
      open += `<${tag}>`;
      close = `</${tag}>` + close;
    } else if (ghost) {
      const color = ghost[1];
      const tag = `bg${color.charAt(0).toUpperCase()}${color.slice(1)}`;
      open += `<${tag}>`;
      close = `</${tag}>` + close;
    }
    if (BOUTEN_MAP[id]) {
      open += `<span class="${BOUTEN_MAP[id]}">`;
      close = "</span>" + close;
    }
    if (id === "s_combine") {
      open += `<span class="text-combine">`;
      close = "</span>" + close;
    }
  });
  return open + text + close;
}
function alignmentFromStyles(styleIds) {
  for (let i = 0; i < styleIds.length; i++) {
    if (ALIGN_STYLES[styleIds[i]]) {
      return {
        alignment: ALIGN_STYLES[styleIds[i]],
        alignStyle: styleIds[i]
      };
    }
  }
  return {
    alignment: "center",
    alignStyle: null
  };
}
function outlineModeFromStyles(styleIds) {
  if (styleIds.includes("d_outline")) {
    return "outline";
  }
  if (styleIds.includes("d_drop")) {
    return "drop";
  }
  if (styleIds.includes("d_none")) {
    return "none";
  }
  return false;
}
function boxingFromParagraphAndSpans(pNodes) {
  const allStyleIds = [];
  ensureArray(pNodes).forEach(p => {
    allStyleIds.push(...parseStyleList(p["@_style"]));
    ensureArray(p.span).forEach(span => {
      allStyleIds.push(...parseStyleList(span["@_style"]));
    });
  });
  const hasBoxedOnP = allStyleIds.some(id => BOXED_RE.test(id) || GHOST_BOXED_RE.test(id));
  // Stripe = boxing style on <p>; box = on spans. Heuristic: if any p has ps_bg_*, it's stripe.
  let stripe = false;
  let ghost = false;
  let boxed = false;
  ensureArray(pNodes).forEach(p => {
    parseStyleList(p["@_style"]).forEach(id => {
      if (BOXED_RE.test(id)) {
        stripe = true;
        boxed = true;
      }
      if (GHOST_BOXED_RE.test(id)) {
        stripe = true;
        ghost = true;
      }
    });
    ensureArray(p.span).forEach(span => {
      parseStyleList(span["@_style"]).forEach(id => {
        if (BOXED_RE.test(id)) {
          boxed = true;
        }
        if (GHOST_BOXED_RE.test(id)) {
          ghost = true;
        }
      });
    });
  });
  if (!boxed && !ghost && !hasBoxedOnP) {
    return false;
  }
  if (stripe && ghost) {
    return "ghostStripe";
  }
  if (stripe) {
    return "stripe";
  }
  if (ghost) {
    return "ghostBoxed";
  }
  return "boxed";
}

/**
 * Convert internal Event HTML into Rosetta <span>…</span> markup (single-line, no pretty print).
 */
function htmlToRosettaSpans(html, options = {}) {
  const defaultBgStyle = options.defaultBgStyle || null;
  let text = html || "";

  // Flatten quill paragraphs to newlines
  text = text.replace(/<\/p>\s*<p\b[^>]*>/gi, "\n").replace(/<p\b[^>]*>/gi, "").replace(/<\/p>/gi, "");

  // Convert ruby to Rosetta nested spans
  text = text.replace(/<ruby([^>]*)>([\s\S]*?)<\/ruby>/gi, (match, attrs, inner) => {
    const align = /data-align=["']around["']/.test(attrs) ? "s_rb_algn_around" : "s_rb_algn_center";
    const rtMatch = inner.match(/<rt\b[^>]*>([\s\S]*?)<\/rt>/i);
    const rubyText = rtMatch ? rtMatch[1] : "";
    const base = inner.replace(/<rt\b[^>]*>[\s\S]*?<\/rt>/i, "").replace(/<\/?[^>]+>/g, "");
    return `<span style="${align}"><span style="s_rb_b">${escapeText(base)}</span><span style="s_rb_t">${escapeText(rubyText)}</span></span>`;
  });

  // text-combine
  text = text.replace(/<span\b[^>]*class=["']text-combine["'][^>]*>([\s\S]*?)<\/span>/gi, (m, inner) => {
    return `<span style="s_combine">${escapeText(inner.replace(/<\/?[^>]+>/g, ""))}</span>`;
  });

  // bouten
  text = text.replace(/<(?:em|span)\b[^>]*class=["'](bouten-[a-z]+)["'][^>]*>([\s\S]*?)<\/(?:em|span)>/gi, (m, cls, inner) => {
    const styleId = BOUTEN_TO_STYLE[cls] || "s_emf_fdo";
    return `<span style="${styleId}">${escapeText(inner.replace(/<\/?[^>]+>/g, ""))}</span>`;
  });

  // Map formatting tags to Rosetta spans — process innermost first via iterative replacement
  const formatMap = [{
    open: /<strong>/gi,
    close: /<\/strong>/gi,
    style: "s_bold"
  }, {
    open: /<b>/gi,
    close: /<\/b>/gi,
    style: "s_bold"
  }, {
    open: /<em>/gi,
    close: /<\/em>/gi,
    style: "s_italic"
  }, {
    open: /<i>/gi,
    close: /<\/i>/gi,
    style: "s_italic"
  }, {
    open: /<u>/gi,
    close: /<\/u>/gi,
    style: "s_underline"
  }];
  Object.keys(FG_COLOR_TO_STYLE).forEach(color => {
    formatMap.push({
      open: new RegExp(`<${color}>`, "gi"),
      close: new RegExp(`</${color}>`, "gi"),
      style: FG_COLOR_TO_STYLE[color]
    });
    const webColor = color === "green" ? "lime" : color;
    const bgTag = `bg${webColor.charAt(0).toUpperCase()}${webColor.slice(1)}`;
    formatMap.push({
      open: new RegExp(`<${bgTag}>`, "gi"),
      close: new RegExp(`</${bgTag}>`, "gi"),
      style: `ps_bg_boxed${webColor}`
    });
    if (color === "green") {
      formatMap.push({
        open: /<bgGreen>/gi,
        close: /<\/bgGreen>/gi,
        style: "ps_bg_boxedlime"
      });
    }
  });

  // Convert color spans style='color: X'
  text = text.replace(/<span\s+style=['"]color:\s*([^;'"]+)[;]?['"][^>]*>([\s\S]*?)<\/span>/gi, (m, color, inner) => {
    const name = String(color).trim().toLowerCase();
    const styleId = FG_COLOR_TO_STYLE[name];
    if (styleId) {
      return `<span style="${styleId}">${inner}</span>`;
    }
    return inner;
  });

  // Simple approach: split by newlines into lines, wrap unwrapped text in spans
  const lines = text.split(/\n|<br\s*\/?>/i);
  const parts = [];
  lines.forEach((line, idx) => {
    if (idx > 0) {
      parts.push("<span><br/></span>");
    }
    let lineHtml = line.trim();
    if (!lineHtml) {
      return;
    }

    // Convert paired format tags to styled spans
    formatMap.forEach(({
      open,
      close,
      style
    }) => {
      // Non-greedy pair replace
      const pairRe = new RegExp(`${open.source}([\\s\\S]*?)${close.source}`, open.flags.includes("i") ? "gi" : "g");
      lineHtml = lineHtml.replace(pairRe, (m, inner) => {
        // If already a span with style, merge
        if (/^<span style="/i.test(inner) && /<\/span>$/i.test(inner)) {
          return inner.replace(/<span style="/i, `<span style="${style} `);
        }
        return `<span style="${style}">${inner}</span>`;
      });
    });

    // Strip remaining unknown tags except span
    lineHtml = lineHtml.replace(/<\/?(?!span\b)[a-z][^>]*>/gi, "");

    // Wrap plain text segments not already in spans
    if (!/^<span\b/i.test(lineHtml) || !/<\/span>$/i.test(lineHtml)) {
      // Split into span chunks and text
      const chunks = [];
      const spanRe = /<span\b[^>]*>[\s\S]*?<\/span>/gi;
      let last = 0;
      let m;
      while ((m = spanRe.exec(lineHtml)) !== null) {
        if (m.index > last) {
          const plain = lineHtml.slice(last, m.index);
          if (plain) {
            chunks.push(wrapPlainWithStyles(plain, defaultBgStyle));
          }
        }
        chunks.push(m[0]);
        last = m.index + m[0].length;
      }
      if (last < lineHtml.length) {
        chunks.push(wrapPlainWithStyles(lineHtml.slice(last), defaultBgStyle));
      }
      lineHtml = chunks.join("") || wrapPlainWithStyles(lineHtml, defaultBgStyle);
    } else if (defaultBgStyle && /<span style="/i.test(lineHtml)) {
      lineHtml = lineHtml.replace(/<span style="/gi, `<span style="${defaultBgStyle} `);
    }
    parts.push(escapeTextNodes(lineHtml));
  });
  return parts.join("");
}
function wrapPlainWithStyles(plain, defaultBgStyle) {
  const escaped = escapeText(plain);
  if (!escaped) {
    return "";
  }
  if (defaultBgStyle) {
    return `<span style="${defaultBgStyle}">${escaped}</span>`;
  }
  return `<span>${escaped}</span>`;
}
function escapeText(text) {
  return rubyMarkup.escapeXml(text);
}

/**
 * XML-escape text nodes while leaving real tags intact.
 * A real tag is "<" or "</" followed by a letter, so "< COUNTRY" stays text.
 * Already-encoded entities (&amp; &lt; &gt; &quot;) are not double-escaped.
 */
function escapeTextNodes(html) {
  if (!html) {
    return html;
  }
  return html.replace(/(<\/?[a-zA-Z][^>]*>)|([^<]+)|(<)/g, (m, tag, text, strayLt) => {
    if (tag) {
      return tag;
    }
    if (strayLt) {
      return "&lt;";
    }
    return escapeXmlPreservingEntities(text);
  });
}
function escapeXmlPreservingEntities(text) {
  return String(text).replace(/&(?!(?:amp|lt|gt|quot|apos|#\d+|#x[\da-f]+);)/gi, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
function clockTimeFromSec(sec) {
  // HH:MM:SS.TTT — Rosetta requires media clock times
  if (sec < 0) {
    sec = 0;
  }
  const hours = Math.floor(sec / 3600);
  const minutes = Math.floor(sec % 3600 / 60);
  const secs = Math.floor(sec % 60);
  let ms = Math.round(sec % 1 * 1000);
  if (ms === 1000) {
    ms = 999;
  }
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}.${String(ms).padStart(3, "0")}`;
}

/**
 * Minimal required + commonly used styles for encode.
 */
function buildStyleBlock(usedStyles, options = {}) {
  const fontSizeRh = options.fontSizeRh || "5.333";
  const defaultFg = options.defaultFg || "s_fg_white";
  const defaultAlign = options.defaultAlign || "p_al_center";
  const defaultOutline = options.defaultOutline || "d_outline";
  const need = new Set(["r_default", "d_default", "_d_default", "_r_default", "_r_quantisationregion", "p_font1", "p_font2", defaultFg, defaultAlign, ...usedStyles]);
  if ([...need].some(s => s === "r_vertical" || s.startsWith("V"))) {
    need.add("r_vertical");
    need.add("_r_vertical");
  }
  const lines = [];
  const add = (id, attrs) => {
    if (need.has(id)) {
      lines.push(`   <style xml:id="${id}"${attrs}/>`);
    }
  };
  add("r_default", ` tts:overflow="visible" tts:wrapOption="noWrap" tts:backgroundColor="#00000000" tts:showBackground="whenActive" tts:fontStyle="normal" tts:fontWeight="normal" tts:fontFamily="proportionalSansSerif" style="_r_default"`);
  add("r_vertical", ` tts:writingMode="tbrl" style="_r_vertical"`);
  add("d_default", ` style="_d_default"`);
  add("d_fillgap", ` itts:fillLineGap="true"`);
  add("d_forced", ` itts:forcedDisplay="true"`);
  add("d_outline", ` style="s_outlineblack"`);
  add("d_drop", ` style="s_dropblack"`);
  add("d_none", ` style="s_noneblack"`);
  add("p_rtl", ` tts:direction="rtl"`);
  [["p_al_start", ` ebutts:multiRowAlign="start" tts:textAlign="start"`], ["p_al_end", ` ebutts:multiRowAlign="end" tts:textAlign="end"`], ["p_al_center", ` ebutts:multiRowAlign="center" tts:textAlign="center"`], ["p_al_start_center", ` ebutts:multiRowAlign="center" tts:textAlign="start"`], ["p_al_start_end", ` ebutts:multiRowAlign="end" tts:textAlign="start"`], ["p_al_end_start", ` ebutts:multiRowAlign="start" tts:textAlign="end"`], ["p_al_end_center", ` ebutts:multiRowAlign="center" tts:textAlign="end"`], ["p_al_center_start", ` ebutts:multiRowAlign="start" tts:textAlign="center"`], ["p_al_center_end", ` ebutts:multiRowAlign="end" tts:textAlign="center"`]].forEach(([id, attrs]) => add(id, attrs));
  add("p_font1", ` tts:fontFamily="proportionalSansSerif" tts:lineHeight="125%" tts:fontSize="100%"`);
  add("p_font2", ` tts:fontFamily="proportionalSansSerif" tts:lineHeight="125%" tts:fontSize="100%"`);
  Object.keys(FG_COLORS).forEach(id => {
    const color = FG_COLORS[id];
    const hex = {
      black: "#000000",
      red: "#FF0000",
      yellow: "#FFFF00",
      lime: "#00FF00",
      green: "#00FF00",
      cyan: "#00FFFF",
      blue: "#0000FF",
      magenta: "#FF00FF",
      white: "#FFFFFF"
    }[color];
    add(id, ` tts:color="${hex}"`);
    add(`ps_bg_boxed${color}`, ` tts:backgroundColor="${hex}"`);
    add(`ps_bg_ghostboxed${color}`, ` tts:backgroundColor="${hex}80"`);
    add(`s_outline${color}`, ` tts:textOutline="${hex} 0.05em"`);
    add(`s_drop${color}`, ` tts:textOutline="${hex} 0.05em"`);
    add(`s_none${color}`, ` tts:textOutline="none"`);
  });
  add("s_italic", ` tts:fontStyle="italic"`);
  add("s_bold", ` tts:fontWeight="bold"`);
  add("s_underline", ` tts:textDecoration="underline"`);
  add("p_rb_res_outside", ` tts:rubyReserve="outside"`);
  add("p_shear", ` tts:shear="16.67%"`);
  add("s_rb_b", ` tts:ruby="base"`);
  add("s_rb_t", ` tts:ruby="text"`);
  add("s_rb_algn_center", ` tts:ruby="container" tts:rubyAlign="center"`);
  add("s_rb_algn_around", ` tts:ruby="container" tts:rubyAlign="spaceAround"`);
  add("s_rb_posn_outside", ` tts:ruby="container" tts:rubyPosition="outside"`);
  add("s_combine", ` tts:textCombine="all"`);
  add("s_emf_fco", ` tts:textEmphasis="filled circle outside"`);
  add("s_emf_fdo", ` tts:textEmphasis="filled dot outside"`);
  add("s_emf_fso", ` tts:textEmphasis="filled sesame outside"`);
  add("s_emf_oco", ` tts:textEmphasis="open circle outside"`);
  add("s_emf_odo", ` tts:textEmphasis="open dot outside"`);
  add("s_emf_oso", ` tts:textEmphasis="open sesame outside"`);
  add("_d_default", ` style="${defaultOutline}"`);
  add("_r_default", ` tts:fontSize="${fontSizeRh}rh" tts:lineHeight="125%" ebutts:linePadding="0.25c" itts:fillLineGap="false" tts:luminanceGain="1.0" style="${defaultFg} ${defaultAlign}"`);
  add("_r_vertical", ` style=""`);
  // Always required
  lines.push(`   <style xml:id="_r_quantisationregion" tts:origin="10% 10%" tts:extent="80% 80%" tts:fontSize="${fontSizeRh}rh" tts:lineHeight="125%"/>`);
  return lines.join("\n");
}
function collectUsedStylesFromSpans(spanHtml) {
  const used = new Set();
  const re = /style="([^"]+)"/g;
  let m;
  while ((m = re.exec(spanHtml)) !== null) {
    m[1].split(/\s+/).forEach(id => used.add(id));
  }
  if (/s_rb_/.test(spanHtml)) {
    used.add("p_rb_res_outside");
    used.add("s_rb_b");
    used.add("s_rb_t");
    used.add("s_rb_algn_center");
  }
  return used;
}
export default {
  FG_COLORS,
  FG_COLOR_TO_STYLE,
  BOUTEN_MAP,
  BOUTEN_TO_STYLE,
  ALIGN_STYLES,
  ALIGN_TO_STYLE,
  DEFAULT_QUANT,
  parseStyleList,
  ensureArray,
  parseQuantisationRegion,
  etherealLineCount,
  lineHeightPercent,
  regionToPosition,
  positionToRegion,
  spansToHtml,
  spanToHtml,
  alignmentFromStyles,
  outlineModeFromStyles,
  boxingFromParagraphAndSpans,
  htmlToRosettaSpans,
  clockTimeFromSec,
  buildStyleBlock,
  collectUsedStylesFromSpans,
  convertToHtml,
  quillClasses,
  rubyMarkup,
  ttmlFunc
};